import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Material UI
import {
    makeStyles,
    TextField,
    IconButton,
} from '@material-ui/core';
import { FetchPolicy } from 'utils/enum/Core';
import { Tabs, Tab } from 'react-bootstrap';
import TabContainer from 'components/widgets/tabs/TabContainer';
import VehicleNoteEditDialog from 'components/widgets/inventory/VehicleNoteEditDialog';
import EditIcon from '@material-ui/icons/Edit';

// HTTP
import { useQuery } from '@apollo/client';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';

import VehicleNoteStyle from 'styles/widgets/inventory/VehicleNoteStyle';
import ModalUtils from 'utils/ModalUtils';
import Permission from 'utils/enum/Permissions';
import KeyStore from 'utils/KeyStore';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => VehicleNoteStyle.notePanel(theme));

const VehicleNote = (props) => {
    const { detail, onUpdateVersion } = props;
    const { stockNumber, version } = detail;

    const classes = useStyles();
    const keyStore = new KeyStore();
    const WRITE = keyStore.hasPermission(Permission.INVENTORY_VEHICLE_WRITE);
    const WRITE_NOTES = keyStore.hasPermission(Permission.INVENTORY_VEHICLE_NOTES_WRITE);

    const [state, setState] = useState({
        openDialog: false,
        dialogLabel: '',
        selectedNote: '',
        notes: null,
        marketingNotes: null,
        marketCompsNotes: null,
        notesBackup: null,
    });

    const {
        openDialog,
        dialogLabel,
        selectedNote,
        notes,
        marketingNotes,
        marketCompsNotes,
        notesBackup,
    } = state;

    const {
        data,
        loading,
        error,
        refetch,
    } = useQuery(InventoryQuery.PULL_NOTES, {
        variables: {
            stockNumber,
        },
        skip: stockNumber == null,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }

        if (!loading) {
            const response = data?.pullNotes || {};
            setState((prevState) => ({
                ...prevState,
                ...response,
                openDialog: false,
                dialogLabel: '',
                selectedNote: '',
                notesBackup: response,
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    const onEditNote = (label, selected) => {
        setState((prevState) => ({
            ...prevState,
            openDialog: !openDialog,
            dialogLabel: label || '',
            selectedNote: selected || '',
            ...(!selected ? {
                [selectedNote]: notesBackup[selectedNote],
            } : {}),
        }));
    };

    const isMarketingNotes = selectedNote === 'marketingNotes';
    const canWrite = selectedNote === 'notes' ? WRITE_NOTES : WRITE;
    return (
        <>
            <div className={classes.main}>
                <TabContainer>
                    <Tabs
                        className={clsx(classes.borderContainer, classes.navbar)}
                    >
                        <Tab className={classes.tab} eventKey={0} title="Notes">
                            {WRITE_NOTES && (
                                <IconButton
                                    className={classes.editButton}
                                    size="small"
                                    onClick={() => onEditNote('Edit Notes', 'notes')}
                                >
                                    <EditIcon fontSize="inherit" />
                                    Edit
                                </IconButton>
                            )}
                            <TextField
                                placeholder="Notes"
                                multiline
                                rows="4"
                                value={notes || ''}
                                margin="normal"
                                variant="outlined"
                                disabled
                            />
                        </Tab>
                        <Tab className={classes.tab} eventKey={1} title="MarketComps">
                            {WRITE && (
                                <IconButton
                                    className={classes.editButton}
                                    size="small"
                                    onClick={() => onEditNote('Edit MarketComps Notes', 'marketCompsNotes')}
                                >
                                    <EditIcon fontSize="inherit" />
                                    Edit
                                </IconButton>
                            )}
                            <TextField
                                placeholder="Notes"
                                multiline
                                rows="4"
                                value={marketCompsNotes || ''}
                                margin="normal"
                                variant="outlined"
                                disabled
                            />
                        </Tab>
                        <Tab className={classes.tab} eventKey={2} title="Marketing Notes for AI">
                            {WRITE && (
                                <IconButton
                                    className={classes.editButton}
                                    size="small"
                                    onClick={() => onEditNote('Edit Marketing Notes for AI', 'marketingNotes')}
                                >
                                    <EditIcon fontSize="inherit" />
                                    Edit
                                </IconButton>
                            )}
                            <TextField
                                placeholder="Example: New Tires, New Brakes, Bedliner."
                                multiline
                                rows="4"
                                value={marketingNotes || ''}
                                margin="normal"
                                variant="outlined"
                                disabled
                            />
                        </Tab>
                    </Tabs>
                </TabContainer>
            </div>
            <VehicleNoteEditDialog
                canWrite={canWrite}
                openDialog={openDialog}
                dialogLabel={dialogLabel}
                selectedNote={selectedNote}
                state={state}
                setState={setState}
                stockNumber={stockNumber}
                version={version}
                refetchNotes={refetch}
                onUpdateVersion={onUpdateVersion}
                onEditNote={onEditNote}
                placeholder={isMarketingNotes ? 'Example: New Tires, New Brakes, Bedliner.' : 'Notes'}
            />
        </>
    );
};

VehicleNote.propTypes = {
    detail: PropTypes.object.isRequired,
    onUpdateVersion: PropTypes.func.isRequired,
};

export default VehicleNote;
